<template>
  <div
    v-if="computeVisibility"
    class="nav-item"
    :class="{ 'nav-item--shrink': isShrink }"
    @mouseenter="isShrink && emit('onMouseover', $event)"
    @mouseleave="isShrink && emit('onMouseleave', $event)"
    @click="emit('handleItem')"
  >
    <div
      v-tooltip="isShrink ? { value: title, position: 'top-right' } : {}"
      class="header"
      :class="{
        selected: selected || (subItemsRef || []).some((el) => el.selected),
      }"
    >
      <div class="left" :class="isShrink ? 'auto' : ''">
        <BaseSvg
          :src="selected ? selectedIcon || icon : icon"
          alt="icon"
          class="item-icon"
        />
        <span v-if="!isShrink" class="item-title">{{ title }}</span>
      </div>
      <span
        v-if="!!subItems?.length && !isShrink"
        class="right"
        :class="{ rotate: showSubItem }"
      >
        <BaseSvg :src="ChevronLeft" alt="down-icon" />
      </span>
    </div>
    <Transition :name="!isShrink ? 'expand' : ''" mode="out-in">
      <div v-if="showSubItem && subItemsRef?.length" class="sub-items">
        <span v-if="isShrink && title" class="item-title"> {{ title }} </span>
        <span
          v-for="item in subItemsRef"
          :key="item.routeKey"
          class="sub-item"
          :class="{ selected: item.selected }"
          @click.stop="emit('handleSubItem', item)"
        >
          <span class="line" />
          <span class="title">{{ item.title }}</span>
        </span>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ChevronLeft } from "@/assets/icons";
import { BaseSvg } from "@/components/Base/index";
import { computed, ref, watch } from "vue";
import { useUserStore } from "@/stores";

export interface INavbarItem {
  title?: string;
  icon?: string;
  selectedIcon?: string;
  subItems?: INavbarItem[];
  selected?: boolean;
  showSubItem?: boolean;
  disabled?: boolean;
  routeKey?: string;
  link?: string;
  isShrink?: boolean;
  visibility?: (item?: INavbarItem) => boolean;
}
const props = withDefaults(defineProps<INavbarItem>(), {
  showSubItem: false,
  isShrink: false,
});
const emit = defineEmits<{
  handleItem: [];
  handleSubItem: [subItem: INavbarItem];
  onMouseover: [event: MouseEvent];
  onMouseleave: [event: MouseEvent];
}>();
const subItemsRef = ref<INavbarItem[]>([]);
const userStore = useUserStore();
watch(
  [() => props.subItems, () => userStore.user],
  ([newVal]) => {
    const newSubItems: INavbarItem[] = [];
    newVal?.forEach((item) => {
      if (item.visibility ? item.visibility?.() : true) {
        newSubItems.push(item);
      }
    });
    subItemsRef.value = newSubItems;
  },
  { deep: true, immediate: true }
);
const computeVisibility = computed(() => {
  return props.visibility ? props.visibility?.() : true;
});
</script>

<style scoped lang="scss">
.nav-item {
  position: relative;
  border-radius: $border-radius-sm;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  &--shrink {
    width: max-content;
    padding: 0 12px;
    margin: 0 -8px;

    & .sub-items {
      position: absolute;
      min-width: 218px;
      padding: 16px;
      background: $background-light;
      border: solid 1px $stroke;
      border-left: none;
      @include box-shadow;
      border-radius: 0 $border-radius-sm $border-radius-sm 0;
      top: -12px;
      z-index: 5;
      left: 48px;
    }
    & .header {
      border-radius: $border-radius-sm;
      &:hover {
        &:not(.selected) {
          &::after {
            background: $secondary-light-200;
            opacity: 1;
            width: 2px;
            visibility: visible;
          }
        }
      }
      &.selected {
        background: transparent;
        &::after {
          background: $secondary;
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }
  & .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    height: 48px;
    width: 100%;
    min-width: 48px;
    padding: 0 8px;
    &::after {
      content: "";
      position: absolute;
      width: 4px;
      height: 100%;
      border-radius: 8px 0 0 8px;
      background: transparent;
      right: 0;
      top: 0;
      opacity: 0;
      visibility: hidden;
    }
    &.selected {
      color: $secondary;
    }
    & .left {
      display: flex;
      align-items: center;
      gap: 14px;
      &.auto {
        margin: auto;
      }
      & .item-icon {
        width: 28px;
        height: 28px;
      }
    }
    & .right {
      display: flex;
      @include transition(all);
      rotate: -90deg;
      &.rotate {
        rotate: 90deg;
      }
    }
  }
  & .item-title {
    font-weight: 500;
    white-space: nowrap;
  }
  & .sub-items {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 12px 0 12px 24px;
    & .sub-item {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      &:hover {
        & .line {
          width: 16px;
          background: $text;
        }
      }
      &.selected {
        color: $secondary-dark;
        & .line {
          background: $secondary-dark;
        }
      }
      & .line {
        height: 2.5px;
        width: 10px;
        background: $text;
        @include transition(all);
      }
      & .title {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  &:hover {
    & .header {
      &:not(.selected) {
        color: rgba($secondary, 0.7);
      }
    }
  }
  .expand-leave-active,
  .expand-enter-active {
    @include transition(all);
    overflow: hidden;
  }
  .expand-enter-to,
  .expand-leave-from {
    max-height: 500px;
  }
  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
    max-height: 0;
  }
}
</style>
